import { Button, Stack, TextInput } from '@qred/components-library';
import {
  validateEmail,
  validatePhoneNumber,
} from '@qred/shared-component-library/src/validators';
import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AMLFields from '~/components/shared/AMLFields/AMLFields';
import { PhoneNumberPlaceholders } from '~/constants/markets';
import { SignerType } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { ISigner } from '~/interfaces/shared';
import { RootState } from '~/store';
import { updateSigner } from '~/store/slices/cardOffer.slice';
import { onChangeInputEvent, onFocusSelectEvent } from '~/types/types';

interface SignerProps {
  signer: ISigner;
  onBlur: (name: onChangeInputEvent | onFocusSelectEvent | string) => void;
}

const Signer = ({ signer, onBlur }: SignerProps) => {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const {
    intl: { market },
    matchedMedia: { mobile },
    cardOffer: {
      form: { signers, applicant },
    },
  } = useSelector((state: RootState) => state);

  const formMethods = useForm<ISigner>({
    defaultValues: signer,
    mode: 'onTouched',
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = formMethods;

  /**
   * useEffect to trigger validation on email and phone fields
   * if they are pre-filled. If any of the fields are pre-filled but not valid,
   * the user will see the error message immediately.
   */
  useEffect(() => {
    signer.email && trigger('email');
    signer.phone && trigger('phone');
  }, [trigger, signer]);

  const saveSigner: SubmitHandler<ISigner> = (formData) => {
    dispatch(updateSigner(formData));

    // TODO: fix this when we have GTM ready
    // dispatch(
    //   pushToGtmLoanAction({
    //     actionName: 'edit_signer_save_button_pressed',
    //   })
    // );
  };

  /**
   * Check if the email already exists on other signers or the applicant
   */
  const emailAlreadyExists = (_email: string) => {
    const otherSigners = signers.filter(
      (_signer) => _signer.signerId !== signer.signerId
    );
    const usedEmails = [
      ...otherSigners.map((_signer) => _signer.email),
      applicant.email,
    ];

    return usedEmails.includes(_email);
  };

  /**
   * Check if the phone number already exists on other signers or the applicant
   */
  const phoneAlreadyExists = (_phone: string) => {
    const otherSigners = signers.filter(
      (_signer) => _signer.signerId !== signer.signerId
    );
    const usedPhones = [
      ...otherSigners.map((_signer) => _signer.phone),
      applicant.phoneNumber,
    ];

    return usedPhones.includes(_phone);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(saveSigner)}>
        <Stack spacing="md">
          <TextInput
            inputMode="tel"
            data-cy="signers_step_phone"
            label={translate('Phone') as string}
            placeholder={PhoneNumberPlaceholders[market]}
            {...register('phone', {
              required: translate('ValidationErrors.Required') as string,
              validate: (value) => {
                const phoneIsValid = validatePhoneNumber(value, market);
                if (!phoneIsValid) {
                  return translate('ValidationErrors.Phone') as string;
                }

                if (value && phoneAlreadyExists(value)) {
                  return translate(
                    'ValidationErrors.PhoneAlreadyUsed'
                  ) as string;
                }

                return true;
              },
              onBlur,
            })}
            error={errors.phone?.message}
          />
          <TextInput
            inputMode="email"
            data-cy="signers_step_email"
            type="email"
            label={translate('Email') as string}
            placeholder={translate('Onboarding.EmailPlaceholder') as string}
            {...register('email', {
              required: translate('ValidationErrors.Required') as string,
              validate: (value) => {
                const emailIsValid = validateEmail(value);
                if (!emailIsValid) {
                  return translate('ValidationErrors.Email') as string;
                }

                if (value && emailAlreadyExists(value)) {
                  return translate(
                    'ValidationErrors.EmailAlreadyUsed'
                  ) as string;
                }

                return true;
              },
              onBlur,
            })}
            error={errors.email?.message}
          />
          {signer.type === SignerType.Signer && (
            <AMLFields trackFieldChange={onBlur} />
          )}
        </Stack>
        <Stack align="center">
          <Button
            mt="2em"
            variant="secondary"
            data-cy="save_signer_button"
            fullWidth={mobile}
            size="md"
            type="submit"
            disabled={isSubmitting || isSubmitSuccessful}
          >
            {translate('Save')}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default Signer;
